/* ============================================================
   Хук WebSocket для чата.
   Подключается к ws(s)://<host>/ws/chat/?token=<JWT>, получает
   входящие сообщения в реальном времени, авто-переподключение.
   Сообщения ОТПРАВЛЯЮТСЯ через REST (надёжно), а этот сокет —
   для ПОЛУЧЕНИЯ (входящие от других + эхо своих).
   ============================================================ */
import { useEffect, useRef, useCallback } from 'react';
import { tokenStore } from '@/api';
import type { ChatMessage } from '@/types';

interface IncomingMessage {
  type: 'message';
  message: ChatMessage;
}
interface IncomingTyping {
  type: 'typing';
  conversation_id: number;
  user_id: number;
}
type Incoming = IncomingMessage | IncomingTyping;

interface Options {
  onMessage: (msg: ChatMessage) => void;
  onTyping?: (conversationId: number, userId: number) => void;
}

export function useChatSocket({ onMessage, onTyping }: Options) {
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closedByUs = useRef(false);

  // Держим колбэки в ref, чтобы не пересоздавать соединение на каждый рендер
  const onMessageRef = useRef(onMessage);
  const onTypingRef = useRef(onTyping);
  useEffect(() => { onMessageRef.current = onMessage; }, [onMessage]);
  useEffect(() => { onTypingRef.current = onTyping; }, [onTyping]);

  const connect = useCallback(() => {
    const token = tokenStore.getAccess();
    if (!token) return;

    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const url = `${proto}//${window.location.host}/ws/chat/?token=${encodeURIComponent(token)}`;

    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onmessage = (ev) => {
      try {
        const data: Incoming = JSON.parse(ev.data);
        if (data.type === 'message') {
          onMessageRef.current(data.message);
        } else if (data.type === 'typing') {
          onTypingRef.current?.(data.conversation_id, data.user_id);
        }
      } catch {
        // игнорируем некорректные кадры
      }
    };

    ws.onclose = () => {
      wsRef.current = null;
      // Авто-переподключение, если закрыли не мы
      if (!closedByUs.current) {
        reconnectRef.current = setTimeout(connect, 3000);
      }
    };

    ws.onerror = () => {
      ws.close();
    };
  }, []);

  useEffect(() => {
    closedByUs.current = false;
    connect();
    return () => {
      closedByUs.current = true;
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      wsRef.current?.close();
    };
  }, [connect]);

  // Отправка через сокет (например, индикатор набора). Сообщения шлём через REST.
  const sendTyping = useCallback((conversationId: number) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ action: 'typing', conversation_id: conversationId }));
    }
  }, []);

  return { sendTyping };
}
